"use client";

import Link from "next/link";
import { useParams } from "next/navigation";

import React from "react";

import {
  Home,
  InfoCircle,
  Plus,
  ShoppingBasket,
} from "@repo/design-system/icons";
import { useTranslation } from "react-i18next";

import { CONTACT_SUPPORT_LINK } from "@core/constants";
import { useZendeskLink } from "@core/hooks/useZendeskLink.ts";

import { NotFoundSearch } from "../NotFoundSearch";

const chipClassName =
  "inline-flex h-11 items-center gap-2 rounded-full border border-borderTertiary bg-backgroundTertiary px-4 text-body font-regular text-contentPrimary transition-colors hover:bg-backgroundSecondary";

export const NotFoundContent = () => {
  const { t } = useTranslation(["message", "button"]);
  const { locale } = useParams<{ locale: string }>();
  const zendeskLink = useZendeskLink();

  const language = locale.split("-")[0];
  const contactSupportHref = `${CONTACT_SUPPORT_LINK}/${language}`;

  return (
    <section className="flex min-h-[80vh] w-full flex-col items-center justify-center bg-backgroundPrimary px-4 py-16 text-center">
      <p className="text-sm font-regular uppercase tracking-[0.2em] text-contentSecondary">
        {t("notfound_error_code", { ns: "message" })}
      </p>

      <h1 className="text-error404 font-bold text-contentPrimary">404</h1>

      <h2 className="mb-3 mt-5 text-2xl font-medium text-contentPrimary sm:text-3xl">
        {t("notfound_title", { ns: "message" })}
      </h2>

      <p className="mx-auto mb-7 max-w-md text-lg font-regular text-pretty text-contentSecondary">
        {t("notfound_description", { ns: "message" })}
      </p>

      <div className="flex w-full justify-center">
        <NotFoundSearch />
      </div>

      <p className="mt-7 text-sm font-regular text-contentTertiary">
        {t("notfound_jump_to", { ns: "message" })}
      </p>

      <nav className="mt-4 flex flex-wrap items-center justify-center gap-3">
        <Link href={`/${locale}`} className={chipClassName}>
          <Home width={18} height={18} />
          {t("notfound_homepage", { ns: "message" })}
        </Link>

        <Link href={`/${locale}/shop`} className={chipClassName}>
          <ShoppingBasket width={18} height={18} />
          {t("notfound_shop", { ns: "message" })}
        </Link>

        <Link href={`/${locale}/sell`} className={chipClassName}>
          <Plus width={18} height={18} />
          {t("notfound_sell_a_bike", { ns: "message" })}
        </Link>

        <a
          href={zendeskLink}
          target="_blank"
          rel="noopener noreferrer"
          className={chipClassName}>
          <InfoCircle width={18} height={18} />
          {t("notfound_help_centre", { ns: "message" })}
        </a>
      </nav>

      <p className="mt-12 text-sm font-regular text-contentTertiary">
        {t("notfound_contact_prefix", { ns: "message" })}{" "}
        <a
          href={contactSupportHref}
          target="_blank"
          rel="noopener noreferrer"
          className="font-regular text-contentTertiary underline">
          {t("contact_support", { ns: "button" })}
        </a>{" "}
        {t("notfound_contact_suffix", { ns: "message" })}
      </p>
    </section>
  );
};
